import { useState } from "react";
import { Link } from "@tanstack/react-router";
import madsPortrait from "@/assets/mads-poulsen.jpg.asset.json";
import { useDismissed } from "@/hooks/use-dismissed";

const DISMISS_KEY = "flaneriet:how-to-read-dismissed";

export function HowToRead() {
  const { ready, dismissed, dismiss } = useDismissed(DISMISS_KEY);
  const [isOpen, setIsOpen] = useState(false);

  if (ready && dismissed) return null;

  const principles = [
    {
      title: "En AI trænet i min smag",
      body: "Jeg har lært en AI op i skrivestil, værdier og emner, jeg holder af. Hver morgen finder den de artikler, hvor sandsynligheden for at jeg tænker \"kæft en spændende artikel\" er så høj som mulig.",
    },
    {
      title: "Også til glasset og lørdag aften",
      body: "Det samme gør den med begivenheder og video. Skal du have et glas vin og se noget lørdag aften, ligger der allerede et par valg klar – frem for YouTubes rabbit hole.",
    },
    {
      title: "Uden for feed og nyhedsbrev",
      body: "Ingen algoritme, ingen faste kilder, ingen notifikationer. Det, der dukker op, er ting du sjældent selv havde fundet – og som ikke kommer fra sociale medier eller endnu et nyhedsbrev.",
    },
  ];

  return (
    <section aria-label="Sådan læses Flâneriet">
      {/* Collapsed header: hidden by default, one click to open */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full flex items-center justify-center gap-2 py-6 md:py-8 border-b border-rule text-left hover:text-ink transition-colors"
        aria-expanded={isOpen}
      >
        <span className="eyebrow">Sådan læses Flâneriet</span>
        <span className="text-muted" aria-hidden="true">
          {isOpen ? "↑" : "↓"}
        </span>
      </button>

      {isOpen && (
        <>
          {/* Kurator-signatur */}
          <div className="pt-4 pb-7 md:pt-5 md:pb-9 flex flex-col md:flex-row items-start md:items-center md:justify-center gap-3.5 md:gap-5">
            <img
              src={madsPortrait.url}
              alt="Mads Poulsen, kurator"
              width={42}
              height={42}
              loading="lazy"
              decoding="async"
              className="w-[42px] h-[42px] rounded-full border border-rule object-cover shrink-0"
              style={{ filter: "grayscale(0.32) sepia(0.22) contrast(0.98) brightness(1.02)" }}
            />
            <p className="font-serif italic font-light text-[14px] md:text-[15.5px] leading-[1.55] text-ink-soft m-0 max-w-2xl">
              Kurateret af <span className="not-italic text-ink">Mads Poulsen</span>
              {" — "}far, trommeslager, udvikler.{" "}
              <Link
                to="/om"
                hash="om-udvaelgelsen"
                className="not-italic text-accent hover:text-accent-dark underline underline-offset-4 decoration-[0.5px] whitespace-nowrap"
              >
                Om udvælgelsen →
              </Link>
            </p>
          </div>

          {/* Tre principper */}
          <div>
            {/* Desktop (lg+): 3 kolonner – hårlinjer fra kant til kant af canvas */}
            <div className="hidden lg:grid grid-cols-3 divide-x divide-rule border-t border-b border-rule">
              {principles.map((p, i) => (
                <div
                  key={p.title}
                  className={`py-10 text-left ${i === 0 ? "pr-10" : i === 2 ? "pl-10" : "px-10"}`}
                >
                  <p className="eyebrow mb-3">{p.title}</p>
                  <p className="font-serif font-light text-[16px] leading-[1.6] text-ink-body m-0">
                    {p.body}
                  </p>
                </div>
              ))}
            </div>

            {/* Tablet (md → lg): stablet, hårlinjer top/bund + mellem */}
            <div className="hidden md:block lg:hidden border-t border-b border-rule divide-y divide-rule">
              {principles.map((p) => (
                <div key={p.title} className="py-8 text-left">
                  <p className="eyebrow mb-3">{p.title}</p>
                  <p className="font-serif font-light text-[16px] leading-[1.65] text-ink-body m-0 max-w-[62ch]">
                    {p.body}
                  </p>
                </div>
              ))}
            </div>

            {/* Mobil: full-bleed snap-carousel – ud til viewport-kant */}
            <div className="md:hidden -mx-6 border-t border-b border-rule">
              <div className="hide-scrollbar overflow-x-auto snap-x snap-mandatory">
                <ul className="flex">
                  {principles.map((p, i) => (
                    <li
                      key={p.title}
                      className={`snap-start shrink-0 basis-[80vw] max-w-[340px] py-8 ${
                        i === 0 ? "pl-6 pr-6" : "border-l border-rule px-6"
                      }`}
                    >
                      <p className="eyebrow mb-3">{p.title}</p>
                      <p className="font-serif font-light text-[15.5px] leading-[1.6] text-ink-body m-0">
                        {p.body}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Diskret dismiss */}
            <div className="pt-6 pb-9 md:pt-7 md:pb-10 text-center">
              <button
                type="button"
                onClick={dismiss}
                className="font-mono text-[10px] tracking-[0.14em] uppercase text-muted hover:text-ink transition-colors"
                aria-label="Skjul denne note fremover"
              >
                Skjul denne note ×
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
